/**
 * Referral API client — TS-012 (FR-2.5, FR-4.1).
 *
 * Pattern: follows parent.ts / dashboard.ts exactly — imports the shared
 * axios instance from '@/lib/api' (which auto-attaches
 * Authorization: Bearer {idToken} via its request interceptor). Do NOT roll
 * a separate auth-header mechanism here.
 *
 * Implements TS-012 (T2, T3): validateReferralCode, getMyReferral,
 * createDebouncedReferralValidator. REWRITTEN by promo-code US-004 (T5):
 * validateReferralCode / createDebouncedReferralValidator are now
 * THREE-STATE (see ReferralValidationResult in @/types/referral) — this is
 * a deliberate, governed replacement (DD-005) of the previous
 * collapse-every-error-to-{valid:false} behaviour, done specifically so the
 * US-005 gate can distinguish a genuine "not recognised" code from an
 * outage and fail OPEN on the latter (AC-004.4 / BR-004.2).
 *
 * Two error philosophies live here, on purpose:
 *   - getMyReferral: errors PROPAGATE (TO-LL-036) so the later Refer-a-Friend
 *     card (TS-016) can render an error/retry state. Never caught here.
 *   - validateReferralCode: ADVISORY (FR-2.5) AND three-state (US-004). A
 *     clean 200 body maps to outcome 'valid'/'invalid'; ANY transport error,
 *     timeout, or non-2xx status (network/timeout/4xx/5xx) resolves to
 *     outcome 'undecidable' (no `data`) and NEVER throws into the register
 *     submit path. Enforces TO-LL-036 (US-004 enforcement_point T5: the
 *     catch branch returns {outcome:'undecidable'}, never a value that could
 *     be mistaken for a confirmed decision) in its documented advisory
 *     exception form — the value is advisory and must never gate signup,
 *     but an outage must also never be misreported as "code is invalid".
 */

import { api } from '@/lib/api';
import type {
  ReferralValidation,
  ReferralValidationResult,
  MyReferral,
  ApplyReferralResult,
} from '@/types/referral';

/**
 * Soft-validate a referral/promo code (API-001). Advisory only — resolves,
 * never throws.
 *
 * Implements promo-code US-004 (T5); AC-004.4; enforces TO-LL-036's
 * fail-safe error-handling principle. A clean 200 body ALWAYS maps to a
 * decisive outcome ('valid' | 'invalid') carrying `data`; ANY error path
 * (network, timeout, 4xx, 5xx) maps to 'undecidable' with NO `data` — this
 * is the DD-005 three-state contract the US-005 gate consumes directly.
 *
 * @param code raw user input (trimmed here; server also trims + uppercases).
 * @returns ReferralValidationResult — see @/types/referral for the full
 *   contract and the US-005 gate-consumption rules.
 */
export async function validateReferralCode(code: string): Promise<ReferralValidationResult> {
  const trimmed = code.trim();
  // Empty input is not a code at all — the page's own non-empty guard means
  // this can never disable anything; report a decisive 'invalid' so callers
  // never have to special-case blank input separately from a real miss.
  if (!trimmed) return { outcome: 'invalid', data: { valid: false } };

  try {
    const response = await api.get<ReferralValidation>('/referral/validate', {
      params: { code: trimmed },
      timeout: 5000, // API-001 performance budget; a timeout throws and lands in the catch below.
    });
    const body = response.data;
    // Defensive: coerce to strict boolean so a malformed body never leaks truthiness.
    const valid = body?.valid === true;
    const data: ReferralValidation = {
      valid,
      kind: body?.kind,
      bonusCoins: typeof body?.bonusCoins === 'number' ? body.bonusCoins : undefined,
      exhausted: typeof body?.exhausted === 'boolean' ? body.exhausted : undefined,
    };
    return { outcome: valid ? 'valid' : 'invalid', data };
  } catch {
    // DD-005 / AC-004.4: network/timeout/4xx/5xx ALL land here. Deliberately
    // NOT { valid: false } — an outage must never be reported as a genuine
    // "code not recognised" signal (TO-LL-036). No `data` is returned.
    return { outcome: 'undecidable' };
  }
}

/**
 * Fetch the authenticated parent's referral code, link and share message (API-002).
 *
 * Implements TS-012 (T2), AC-TS012.2. Errors propagate (TO-LL-036) for the
 * later Refer-a-Friend card's (TS-016) error/retry state — NOT caught here.
 *
 * @returns Promise resolving to MyReferral
 */
export async function getMyReferral(): Promise<MyReferral> {
  const response = await api.get<MyReferral>('/referral/me');
  return response.data;
}

/**
 * Submit a captured referral code to the dedicated idempotent apply-referral
 * endpoint (API-003, google-sign-in/US-005). Called AFTER the OAuth callback
 * has provisioned the parent (US-002) — this is the federated door's
 * equivalent of the referrer reward that /auth/register grants natively.
 *
 * Implements US-005 (T3); AC-005.2. Error philosophy: PROPAGATES (TO-LL-036,
 * mirrors getMyReferral above) — this client does NOT swallow a real HTTP
 * failure. The best-effort / never-block-routing decision belongs to the
 * OAuthCallbackPage caller (T5), not to this transport-level client.
 *
 * @param code the referral code captured pre-redirect (raw; server trims +
 *   uppercases before lookup).
 * @returns Promise resolving to { applied } — true only if the referrer was
 *   credited on this call.
 */
export async function applyReferral(code: string): Promise<ApplyReferralResult> {
  const response = await api.post<ApplyReferralResult>('/referral/apply', {
    referralCode: code,
  });
  // Defensive: coerce to a strict boolean so a malformed body never leaks truthiness.
  return { applied: response.data?.applied === true };
}

/**
 * Debounce factory for register-page inline validation (AC-TS012.3).
 * Collapses rapid keystrokes so only the settled value triggers ONE network
 * call; all queued callers within the debounce window resolve with that
 * single result.
 *
 * Implements TS-012 (T3), AC-TS012.3. RETYPED by promo-code US-004 (T5) to
 * the three-state ReferralValidationResult; the underlying validator
 * (validateReferralCode) already never throws, but the belt-and-braces
 * .catch below is kept consistent with the new type — any unexpected
 * rejection also settles as 'undecidable', never a decisive value.
 *
 * @param delayMs debounce window (default 400ms).
 * @param validator injectable for testing (defaults to validateReferralCode).
 * @returns a debounced (code) => Promise<ReferralValidationResult>.
 */
export function createDebouncedReferralValidator(
  delayMs = 400,
  validator: (code: string) => Promise<ReferralValidationResult> = validateReferralCode
): (code: string) => Promise<ReferralValidationResult> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let waiters: Array<(v: ReferralValidationResult) => void> = [];

  return (code: string): Promise<ReferralValidationResult> =>
    new Promise<ReferralValidationResult>((resolve) => {
      waiters.push(resolve);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const settle = waiters;
        waiters = [];
        timer = undefined;
        validator(code)
          .then((r) => settle.forEach((w) => w(r)))
          // DD-005: even an unexpected validator rejection settles as
          // 'undecidable', never a decisive 'invalid' (AC-004.4).
          .catch(() => settle.forEach((w) => w({ outcome: 'undecidable' })));
      }, delayMs);
    });
}
